<template>
  <label :class="b(modifiers)"
         @mouseenter="hover = true"
         @mouseleave="hover = false"
  >
    <input
      v-bind="$attrs"
      v-model="internalValue"
      :class="b('field')"
      :aria-checked="isChecked ? 'true' : 'false'"
      :value="value"
      :name="name"
      type="checkbox"
      @blur="onBlur"
      @focus="onFocus"
      @change="onChange($event, value)"
    >
    <span :class="b('indicator')"></span>
    <span :class="b('label-text')">
      <slot></slot>
    </span>
  </label>
</template>

<script lang="ts">
  import { defineComponent, PropType, toRefs } from 'vue';
  import useFormStates, { FormStates, withProps } from '@/compositions/form-states';
  import { Modifiers } from '@/plugins/vue-bem-cn/src/globals';

  type Value = boolean | string | number;
  type GroupValue = boolean | string[] | number[];

  /**
   * Checkbox component for form elements.
   * Can be used as single element with a Boolean value or multiple checkboxes with an Array.
   */
  export default defineComponent({
    name: 'e-checkbox',

    inheritAttrs: false,

    props: {
      ...withProps(),

      /**
       * The model value to be used for v-model.
       */
      modelValue: {
        type: [Boolean, Array] as PropType<any>, // eslint-disable-line @typescript-eslint/no-explicit-any -- was not able to find a better solution, since the value is very dynamic
        required: true,
      },

      /**
       * Adds name attribute.
       */
      name: {
        type: String,
        required: true,
      },

      /**
       * Adds value attribute.
       */
      value: {
        type: [Boolean, String, Number] as PropType<any>, // eslint-disable-line @typescript-eslint/no-explicit-any -- was not able to find a better solution, since the value is very dynamic
        required: true,
      },

      /**
       * Allows to set the desired layout variant.
       */
      variant: {
        type: String,
        default: 'default',
        validator: (value: string) => [
          'default',
          'toggle',
        ].includes(value),
      },
    },

    // eslint-disable-next-line vue/require-emit-validator -- Disabled because value is of type 'unknown'
    emits: ['update:modelValue', 'change', 'focus', 'blur'],

    setup(props): FormStates {
      return {
        ...useFormStates(toRefs(props).state),
      };
    },

    // data() {
    //   return {};
    // },

    computed: {
      /**
       * Returns a configuration Object for modifier classes.
       */
      modifiers(): Modifiers {
        return {
          ...this.stateModifiers,
          variant: this.variant,
        };
      },

      /**
       * Sets value of component model to parent model
       */
      internalValue: {
        get(): GroupValue {
          return this.modelValue;
        },
        set(value: GroupValue) {
          /**
           * Emits checkbox value e.g. true/false or value
           */
          this.$emit('update:modelValue', value);
        },
      },

      /**
       * Evaluates if the checkbox is currently selected.
       */
      isChecked() {
        return Array.isArray(this.value)
          ? this.value.includes(this.modelValue)
          : this.value;
      },
    },
    // watch: {},

    // beforeCreate() {},
    // created() {},
    // beforeMount() {},
    // mounted() {},
    // beforeUpdate() {},
    // updated() {},
    // activated() {},
    // deactivated() {},
    // beforeUnmount() {},
    // unmounted() {},

    methods: {
      /**
       * Emits focus to parent and wrapper component.
       * Update "hasFocus" state.
       */
      onFocus() {
        this.focus = true;

        /**
         * Focus event
         */
        this.$emit('focus');
      },

      /**
       * Emits blur to parent and wrapper component.
       * Update "hasFocus" state.
       */
      onBlur() {
        this.focus = false;

        /**
         * Blur event.
         */
        this.$emit('blur');
      },

      /**
       * Emits change to parent.
       * This is for reacting to a specific checkbox value instead
       * of the internal value.
       */
      onChange(event: Event, value: Value) {
        const target = event.target as HTMLInputElement;

        this.$emit('change', value, target.checked);
      },
    },
    // render() {},
  });
</script>

<style lang="scss">
  @use 'sass:math';
  @use '../setup/scss/mixins';
  @use '../setup/scss/variables';

  .e-checkbox {
    $this: &;
    $label-size: 19px;

    @include mixins.font(variables.$font-size--16, 22px);

    position: relative;
    display: block;
    cursor: pointer;

    &__field {
      position: absolute;
      left: -200vw;
      -webkit-appearance: none;
    }

    &--variant-default {
      display: flex;
      align-items: center;

      #{$this}__indicator {
        position: relative;
        display: flex;
        flex: 0 0 auto;
        align-items: center;
        width: $label-size;
        height: $label-size;
        margin-left: 2px;
        outline: 2px solid variables.$color-grayscale--0;
        background: variables.$color-grayscale--1000;

        &::before {
          content: '';
          width: $label-size;
          height: $label-size;
          opacity: 0;
          border: 1px solid transparent;
          background: variables.$color-primary--1;
          transform: scale(0);
          transition-timing-function: ease-in-out;
          transition-duration: 100ms;
          transition-property: opacity, transform;
          inset: 0;
        }
      }

      #{$this}__field:checked {
        ~ #{$this}__indicator::before {
          opacity: 1;
          transform: scale(0.75);
        }
      }
    }

    &--variant-toggle {
      $toggle--size: 1rem;

      display: flex;
      align-items: baseline;
      cursor: pointer;

      #{$this}__indicator {
        position: relative;
        width: 1.5 * $toggle--size;
        height: math.div($toggle--size, 3) * 2;
        margin: 0 math.div($toggle--size, 2);
        border-radius: $toggle--size;
        background-color: variables.$color-grayscale--400;
        transition: background-color variables.$transition-duration--300;

        &::before {
          position: absolute;
          top: 50%;
          left: math.div($toggle--size, -2);
          content: '';
          width: $toggle--size;
          height: $toggle--size;
          border-radius: 50%;
          background-color: variables.$color-grayscale--1000;
          box-shadow: 0 1px 2px 1px rgba(variables.$color-grayscale--0, 0.2);
          transform: translateY(-50%);
          transition: left variables.$transition-duration--300;
        }
      }

      #{$this}__field {
        &:checked ~ #{$this}__indicator {
          &::before {
            left: calc(100% - (#{$toggle--size} / 2));
            background-color: variables.$color-status--success;
            transform: translateY(-50%);
          }
        }
      }
    }

    &__label-text {
      display: block;
      margin: 0;
      padding-left: variables.$spacing--15;

      &:hover,
      &:focus {
        color: variables.$color-grayscale--0;

        &::before {
          border-color: variables.$color-grayscale--0;
        }
      }
    }

    &__field:checked {
      ~ #{$this}__label-text {
        color: variables.$color-grayscale--0;
      }
    }

    #{$this}__field:disabled {
      ~ #{$this}__indicator {
        outline-color: variables.$color-grayscale--400;
      }
    }

    #{$this}__field:checked:disabled {
      ~ #{$this}__indicator:before {
        background: variables.$color-grayscale--400;
      }
    }

    &__field:disabled + &__label-text {
      cursor: default;
      color: variables.$color-grayscale--400;

      &:hover,
      &:focus {
        color: variables.$color-grayscale--400;

        &::before {
          border-color: variables.$color-grayscale--400;
        }
      }

      &::before {
        border-color: variables.$color-grayscale--400;
      }
    }

    &__field:checked:disabled + &__label-text {
      &::after {
        background: variables.$color-grayscale--400;
      }
    }
  }
</style>
